import type { ComponentType, ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { shadows } from '../../../theme/shadows';
import { tokens } from '../../../theme/tokens';

interface AgentToggleRowProps {
  label: string;
  icon: ComponentType<{ size: number; color: string; strokeWidth: number }>;
  iconColor: string;
  enabled: boolean;
  onPress: () => void;
  onLabel?: string;
  offLabel?: string;
  rightContent?: ReactNode;
}

export function AgentSwitch({
  enabled,
  onLabel = 'On',
  offLabel = 'Off',
}: Readonly<{ enabled: boolean; onLabel?: string; offLabel?: string }>) {
  return (
    <HStack spacing="xs" align="center">
      <View
        style={[
          styles.track,
          enabled ? styles.trackOn : styles.trackOff,
          enabled && shadows.glowHaloSoft,
        ]}
      >
        <View style={[styles.knob, enabled ? styles.knobOn : styles.knobOff]} />
      </View>
      <Text
        variant="caption"
        color={enabled ? colors.primary[400] : colors.text.tertiary}
        style={styles.switchLabel}
      >
        {enabled ? onLabel : offLabel}
      </Text>
    </HStack>
  );
}

export function AgentToggleRow({
  label,
  icon: Icon,
  iconColor,
  enabled,
  onPress,
  onLabel = 'On',
  offLabel = 'Off',
  rightContent,
}: Readonly<AgentToggleRowProps>) {
  return (
    <Box
      pressable
      onPress={onPress}
      borderRadius="md"
      padding="sm"
      backgroundColor={enabled ? colors.background.tertiary : 'transparent'}
      style={styles.row}
      accessibilityRole="switch"
      accessibilityState={{ checked: enabled }}
      accessibilityLabel={`${label}: ${enabled ? onLabel : offLabel}`}
    >
      <HStack spacing="sm" align="center" justify="between">
        <HStack spacing="sm" align="center" style={styles.content}>
          <Box
            backgroundColor={colors.background.tertiary}
            borderRadius="full"
            alignItems="center"
            justifyContent="center"
            style={styles.iconBox}
          >
            <Icon size={14} color={iconColor} strokeWidth={2} />
          </Box>
          <Text
            variant="caption"
            color={enabled ? colors.text.primary : colors.text.tertiary}
            style={{ fontWeight: '500' }}
            numberOfLines={1}
          >
            {label}
          </Text>
        </HStack>
        {rightContent ?? <AgentSwitch enabled={enabled} onLabel={onLabel} offLabel={offLabel} />}
      </HStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  row: {
    minHeight: 40,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  iconBox: {
    width: 28,
    height: 28,
  },
  track: {
    width: 36,
    height: 20,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    justifyContent: 'center',
  },
  trackOn: {
    backgroundColor: colors.primary[500],
    borderColor: 'transparent',
  },
  trackOff: {
    backgroundColor: colors.surface.row,
    borderColor: colors.border.muted,
  },
  knob: {
    position: 'absolute',
    top: 2,
    width: 14,
    height: 14,
    borderRadius: tokens.radius.full,
  },
  knobOn: {
    left: 18,
    backgroundColor: colors['primary-foreground'],
  },
  knobOff: {
    left: 2,
    backgroundColor: colors.text.secondary,
  },
  switchLabel: {
    fontWeight: '600',
    minWidth: 22,
  },
});
